import { FC, useEffect, useMemo, useState } from "react";
import DualListboxPagedLHS from "../../../components/dualListboxPagedLHS";
import svgIcons from "../../../assets/svg/svgIcons";
import { TCustomizableItemList } from "../../../components/types";
import { useTranslation } from "react-i18next";
import React from "react";
import { useDebouncedCallback } from "use-debounce";
import { APP_CONFIG } from "../../../constants/constants";
import Pagination, { TPaginationSelected } from "../../../components/pagination";
import { useGetIncidentConfigQuery, useModifyIncidentConfigMutation } from "../../../api/network/incidentApiServices";
import { IncidentItem } from "./type";
import HeaderView from "../../../components/admin/headerView";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { useNavigate } from "react-router-dom";

const ScreenAdminIncidentReview: FC = () => {
    const leftContainerRef = React.useRef<HTMLDivElement>(null);
    const { t: tMain } = useTranslation();
    const { t } = useTranslation("translation", {
        keyPrefix: "admins.incident.incident_config",
    });
    const navigate = useNavigate();

    const [userCanEdit, setUserCanEdit] = React.useState<boolean>(false);
    const [searchKeyword, setSearchKeyword] = React.useState<string>("");
    const [currentPage, setCurrentPage] = React.useState<number>(0);
    const lowerSearchKeyword = searchKeyword.toLowerCase();
    const { data: eventStatusCodes, isFetching: isFetchingStatusCodes } = useGetIncidentConfigQuery({});
    const [updateIncident] = useModifyIncidentConfigMutation();
    const { getUserACLAccessLevel } = useACL();
    const thisOrgIncidentReviewAllowed = useLoggedInUserData("incidentreviewallowed");

    useEffect(() => {
        if (
            thisOrgIncidentReviewAllowed !== "1" ||
            getUserACLAccessLevel(ACL_NAMES.ACL_INCIDENT_CONFIG) < ROLE_ACCESS_LEVELS.READ
        ) {
            navigate(routeUrls.dashboard);
        }
    }, []);

    const editIncidentForm = () => {
        setUserCanEdit(true);
    }

    const [initialRightListData, setInitialRightListData] = React.useState<
        TCustomizableItemList[]
    >([]);
    const [rightListItems, setRightListItems] = React.useState<
        TCustomizableItemList[]
    >([]);
    const [allLeftListData, setAllLeftListData] = useState<TCustomizableItemList[]>([]);

    const allItems = useMemo(() => {
        if (!eventStatusCodes) return [];
        return [
            ...eventStatusCodes.available,
            ...eventStatusCodes.selected,
        ].map(field => ({
            id: field.statuscode.toString(),
            title: field.description,
            sub_title: field.statuscode.toString(),
            isactive: 1,
        }));
    }, [eventStatusCodes]);

    useEffect(() => {
        if (isFetchingStatusCodes || !eventStatusCodes)
            return;
        const leftList = eventStatusCodes?.available.map((field: IncidentItem) => ({
            id: field.statuscode.toString(),
            title: field.description,
            sub_title: field.statuscode.toString(),
            isactive: 1,
        }));
        const rightList = eventStatusCodes?.selected.map((field: IncidentItem) => ({
            id: field.statuscode.toString(),
            title: field.description,
            sub_title: field.statuscode.toString(),
            isactive: 1,
        })) ?? []
        setAllLeftListData(leftList)
        setInitialRightListData(rightList)
        setRightListItems(rightList)
    }, [eventStatusCodes, isFetchingStatusCodes])

    const allFilteredLeftListData = useMemo(
        () =>
            allLeftListData.filter((field: TCustomizableItemList) =>
                lowerSearchKeyword === "" ? true :
                    field.id
                        .toLocaleLowerCase()
                        .includes(lowerSearchKeyword) ||
                    field.title
                        .toLocaleLowerCase()
                        .includes(lowerSearchKeyword)),
        [allLeftListData, lowerSearchKeyword]
    );

    useEffect(() => {
        const rightIds = new Set(rightListItems.map(x => x.id));
        setAllLeftListData(
            allItems
                .filter(item => !rightIds.has(item.id))
                .sort((a, b) => a.title.localeCompare(b.title))
        );
    }, [rightListItems, allItems]);
   
    // Calculate paginated data
    const pageSize = APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE;
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const leftListData: TCustomizableItemList[] = allFilteredLeftListData.slice(startIndex, endIndex);

    const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
        setSearchKeyword(value);
        setCurrentPage(0); // Reset to first page on search
        leftContainerRef.current?.scrollTo({ top: 0 });
    }, 500);

    const handleCancelButton = () => {
        setUserCanEdit(false);
    };

    const handleEditIncidentList = async () => {
        try {
            await updateIncident({
                statuscodes: rightListItems.map((item) => Number(item.id)),
            }).unwrap();
            setUserCanEdit(false);
        } catch (err) {
            console.error("Edit incident list failed", err);
        }
    }

    return (
        <div className="flex-1 overflow-y-auto">
            <HeaderView title={t("heading")} />
            <h2 className="text-accent-blue-dark text-base font-semibold leading-6 px-6">
                {t("sub_heading")}
            </h2>
            <div className="px-6 mt-4 flex flex-row justify-between items-center">
                <span className="text-xl font-bold leading-6">{t("listing_heading")}</span>
                <AdminFormFieldSubmit
                    buttonDimensionClass={"h-10 w-full px-10 group"}
                    type="button"
                    variant="primary"
                    onClick={editIncidentForm}
                    label="Edit"
                    disabled={(getUserACLAccessLevel(ACL_NAMES.ACL_INCIDENT_CONFIG) <
                        ROLE_ACCESS_LEVELS.WRITE) || userCanEdit
                    } />
            </div>

            <div
                className={`px-5 py-2 col-span-5 grid grid-cols-12 gap-8 relative mx-2 ${isFetchingStatusCodes ? "opacity-50" : ""}`}
            >
                {isFetchingStatusCodes && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                        <img
                            src={svgIcons.LoadingAnimation}
                            alt="loading-animation"
                            className="w-20 h-20"
                        />
                    </div>
                )}
                <DualListboxPagedLHS
                    leftListContainerRef={leftContainerRef}
                    leftLoading={isFetchingStatusCodes}
                    leftTitle={`${t("left_list_heading")} (${allFilteredLeftListData.length})`}
                    leftSearchKeyword={searchKeyword}
                    leftSearchPlaceholder={t("search_placeholder")}
                    leftSearchCallback={(e: React.ChangeEvent<HTMLInputElement>) =>
                        debouncedSetSearchKeyword(e.target.value)
                    }
                    leftList={leftListData}
                    LeftPaginationComponent={
                        allFilteredLeftListData.length > 0 && (
                            <Pagination
                                pageSize={APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE}
                                totalPages={Math.ceil(allFilteredLeftListData.length / APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE)}
                                forcePage={currentPage}
                                handlePageClick={(data: TPaginationSelected) => {
                                    setCurrentPage(data.selected);
                                    leftContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                onlyPageChange={true}
                                size={1}
                                wrapperClass="flex items-center justify-center"
                            />
                        )
                    }
                    rightTitle={`${t("right_list_heading")} (${rightListItems.length ?? 0})`}
                    rightSearchPlaceholder={t("search_placeholder")}
                    // rightSearchCallback={handleRightSearchChange} // disabled to let the default (within DualListbox component) search callback run
                    initialRightList={initialRightListData}
                    rightListItems={rightListItems}
                    setRightListItems={setRightListItems}
                    handleCancelButton={handleCancelButton}
                    handleSaveButton={handleEditIncidentList}
                    groupPage={true}
                    showInactiveEntities={false}
                    showInactiveEntitiesText={false}
                    saveButtonText={tMain("save")}
                    showStatusTagOnItems={false}
                    displayRightSideOnly={false}
                    leftFilterItemsBasedOnRightList={true}
                    isAddAllDisabled={true}
                    disabled={!userCanEdit}
                />
            </div>
        </div>
    )
}

export default ScreenAdminIncidentReview;